import { useMemo, useState } from 'react';
import type { GmbAuditReport } from '@/types/gmb';
import { buildConsultContext } from '@/pages/gmb-audit/consult/utils/consultContext';
import ReportInlineCta from '@/pages/gmb-audit/report/components/ReportInlineCta';
import ReportIdentityHeader from '@/pages/gmb-audit/report/components/ReportIdentityHeader';
import ReportHealthOverview from '@/pages/gmb-audit/report/components/ReportHealthOverview';
import ReportDimensionsSection from '@/pages/gmb-audit/report/components/ReportDimensionsSection';
import ReportBenchmarkSection from '@/pages/gmb-audit/report/components/ReportBenchmarkSection';
import ReportSamplePositionSection from '@/pages/gmb-audit/report/components/ReportSamplePositionSection';
import ReportPrioritySection from '@/pages/gmb-audit/report/components/ReportPrioritySection';
import ReportMetricsSection from '@/pages/gmb-audit/report/components/ReportMetricsSection';
import ReportPeerSummarySection from '@/pages/gmb-audit/report/components/ReportPeerSummarySection';
import ReportCoverageSection from '@/pages/gmb-audit/report/components/ReportCoverageSection';
import ReportMethodologySection from '@/pages/gmb-audit/report/components/ReportMethodologySection';
import ReportSaveSection from '@/pages/gmb-audit/report/components/ReportSaveSection';
import type { SaveStatus, ReadbackStatus } from '@/pages/gmb-audit/report/components/ReportSaveSection';
import ReportSavedBanner from '@/pages/gmb-audit/report/components/ReportSavedBanner';
import ReportHistorySection from '@/pages/gmb-audit/report/components/ReportHistorySection';
import ReportCtaSection from '@/pages/gmb-audit/report/components/ReportCtaSection';

/**
 * 正式「Google 商家快速健診報告」（Batch 6-B2；7-B2B 擴充歷史讀回；UI-2A Dashboard 化）。
 * 唯一資料來源 = GmbAuditReport（target / comparisonCategory / peerComparison / scoreResult）。
 * 全部沿用 Backend AuditScoreResult；前端不重算 score / grade / coverage / priority / metric status。
 *
 * UI-2A 報告最上方新順序：
 *   01 Report Identity Header
 *   02 Health Overview Dashboard（Score Visual + KPI）
 *   03 4 大診斷構面
 *
 * UI-2B 報告中段新順序（減少文字、增加比較與行動感）：
 *   04 你和附近同業差在哪裡？（Rating / Review Benchmark）
 *   05 本次同業樣本脈絡（Peer Context）
 *   06 本次同業樣本相對位置（Sample Position）
 *   07 現在先做這 3 件事（既有 priorities）
 *   08 11 項健診明細（4 組 Accordion）
 *
 * UI-2C 報告尾段新順序（Coverage 瘦身 + Methodology 收合 + Save / History / Trend / CTA）：
 *   09 Coverage（compact summary）
 *   10 Methodology（折疊 + 固定免責說明）
 *   11 Save（保存這次健診）
 *   12 此瀏覽器健診紀錄（列表 + 健康度趨勢 + 前後比較）
 *   13 CTA
 *
 * mode = 'live'  ：本次 session 即時報告，顯示保存區塊（含讀取已保存報告）。
 * mode = 'saved' ：歷史 Snapshot 讀回，頂部顯示「已保存報告」badge + 保存時間，隱藏保存區塊。
 */
export default function AuditReportView({
  report,
  onRestart,
  saveStatus,
  saveError,
  saveReused,
  onSave,
  reportToken,
  readbackStatus,
  readbackError,
  onReadback,
  mode = 'live',
  savedAt = '',
  legacySavedReport = false,
  onReturnToLive = () => {},
  onOpenReportFromHistory,
}: {
  report: GmbAuditReport;
  onRestart: () => void;
  saveStatus: SaveStatus;
  saveError: string | null;
  saveReused: boolean;
  onSave: () => void;
  reportToken: string | null;
  readbackStatus: ReadbackStatus;
  readbackError: string | null;
  onReadback: () => void;
  mode?: 'live' | 'saved';
  savedAt?: string;
  /** 讀回的歷史報告建立於歷史追蹤功能啟用前（無 history scope）。 */
  legacySavedReport?: boolean;
  onReturnToLive?: () => void;
  /** 歷史清單「查看該次報告」→ 以 reportToken 讀回。 */
  onOpenReportFromHistory: (reportToken: string) => void;
}) {
  const isSaved = mode === 'saved';
  // 即時報告以元件掛載時間作為「本次健診日期」；歷史報告沿用保存時間。
  const [liveReportDate] = useState(() => new Date().toISOString());
  const reportDate = isSaved && savedAt ? savedAt : liveReportDate;

  // V1 Conversion-1：由既有報告結果組出諮詢頁要帶的 context（不重算、不重新查 Google）。
  const consultContext = useMemo(() => buildConsultContext(report), [report]);

  return (
    <div className="pb-4">
      {isSaved && (
        <ReportSavedBanner
          savedAt={savedAt}
          legacy={legacySavedReport}
          onReturnToLive={onReturnToLive}
        />
      )}

      {/* 01｜Report Identity Header */}
      <ReportIdentityHeader
        target={report.target}
        reportDate={reportDate}
        peerSampleCount={report.peerComparison?.sampleCount ?? null}
      />

      {/* 02｜Health Overview Dashboard */}
      <ReportHealthOverview result={report.scoreResult} peerComparison={report.peerComparison} />

      {/* 03｜4 大診斷構面 */}
      <ReportDimensionsSection result={report.scoreResult} />

      {/* CTA #1（light）｜Health Overview + 4 大構面後 */}
      <ReportInlineCta
        variant="light"
        title="想知道這份健診結果代表什麼？"
        body="分數可以幫你快速看出缺口，但真正改善仍要依商家現況、產業與目前經營方式安排順序。"
        primaryLabel="預約健診解讀"
        context={consultContext}
      />

      {/* 04｜你和附近同業差在哪裡？（Benchmark） */}
      <ReportBenchmarkSection target={report.target} peerComparison={report.peerComparison} />

      {/* 05｜本次同業樣本脈絡（Peer Context） */}
      <ReportPeerSummarySection
        peerComparison={report.peerComparison}
        comparisonCategory={report.comparisonCategory}
      />

      {/* 06｜本次同業樣本相對位置（Sample Position） */}
      <ReportSamplePositionSection target={report.target} peerComparison={report.peerComparison} />

      {/* 07｜現在先做這 3 件事（既有 priorities，不重新選 Top 3） */}
      <ReportPrioritySection priorities={report.scoreResult.priorities} metrics={report.scoreResult.metrics} />

      {/* CTA #2（medium）｜Top 3 之後 */}
      <ReportInlineCta
        variant="medium"
        title="知道問題了，但不知道怎麼開始？"
        body="不一定要一次全部重做。可以先從最影響目前接單與信任的項目開始，逐步安排改善。"
        primaryLabel="讓顧問幫我排改善順序"
        context={consultContext}
      />

      {/* 08｜11 項健診明細（4 組 Accordion） */}
      <ReportMetricsSection metrics={report.scoreResult.metrics} target={report.target} />

      <ReportCoverageSection result={report.scoreResult} peerComparison={report.peerComparison} />
      <ReportMethodologySection />

      {!isSaved && (
        <>
          <ReportSaveSection
            status={saveStatus}
            errorMessage={saveError}
            reused={saveReused}
            onSave={onSave}
            reportToken={reportToken}
            readbackStatus={readbackStatus}
            readbackError={readbackError}
            onReadback={onReadback}
          />
          <ReportHistorySection
            targetPlaceId={report.target.placeId}
            saveStatus={saveStatus}
            onOpenReport={onOpenReportFromHistory}
          />
        </>
      )}
      <ReportCtaSection onRestart={onRestart} context={consultContext} />
    </div>
  );
}